import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useUpdateContact, useResolveContactField } from "@/hooks/use-api";
import type { UnresolvedFieldEntry } from "@adva/types";

// ADV-1101: inline-fix Sheet for a single unresolved contact field (email
// or phone). Two primary actions:
//
//   1. Replace value — controlled input, validated client-side, calls
//      useUpdateContact. On success the unresolved entry is cleared and
//      the sheet closes; React Query re-fetches the contact.
//   2. Keep as-is — calls useResolveContactField with action: "keep_raw".
//      Promotes the raw_value verbatim to the clean column. Useful when
//      the import-flagged value is actually correct (some emails do look
//      junk) or when there's no better value to put in its place.
//
// Errors are inline `<Alert>`; success is signalled by the sheet closing
// (no toast — matches the existing edit-sheet pattern across v2).

type Field = "email" | "phone";

const FIELD_LABELS: Record<Field, { title: string; helper: string }> = {
  email: { title: "Fix email", helper: "Imported email" },
  phone: { title: "Fix phone", helper: "Imported phone" },
};

// Permissive email regex — matches the relaxed input expectation. The
// server's `optionalEmail()` is the source of truth; this client-side
// check is for fast feedback only.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function FixContactFieldSheet({
  contactId,
  field,
  entry,
  open,
  onClose,
}: {
  contactId: string;
  field: Field;
  entry: UnresolvedFieldEntry | null;
  open: boolean;
  onClose: () => void;
}) {
  const updateContact = useUpdateContact();
  const resolveField = useResolveContactField();
  const labels = FIELD_LABELS[field];

  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(entry?.raw_value ?? "");
      setError(null);
    }
  }, [open, entry?.raw_value]);

  const submitting = updateContact.isPending || resolveField.isPending;

  function validate(): string | null {
    if (field === "email" && value.trim() !== "" && !EMAIL_RE.test(value)) {
      return "Enter a valid email address.";
    }
    return null;
  }

  async function handleReplace(e: React.FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    try {
      await updateContact.mutateAsync({
        id: contactId,
        input: { [field]: value.trim() === "" ? null : value.trim() },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  async function handleKeepAsIs() {
    setError(null);
    try {
      await resolveField.mutateAsync({
        id: contactId,
        input: { field, action: "keep_raw" },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-md">
        <form onSubmit={handleReplace} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{labels.title}</SheetTitle>
            <SheetDescription>
              {labels.helper}:{" "}
              <span className="font-mono">{entry?.raw_value ?? "—"}</span>
              {entry?.reason ? (
                <>
                  . Reason:{" "}
                  <span className="text-[color:var(--color-text-md)]">
                    {entry.reason}
                  </span>
                  .
                </>
              ) : null}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 flex-1 space-y-3">
            <label
              htmlFor={`fix-${field}`}
              className="typo-label text-[color:var(--color-text-lo)]"
            >
              {field === "email" ? "Email" : "Phone"}
            </label>
            <Input
              id={`fix-${field}`}
              name={field}
              type={field === "email" ? "email" : "tel"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              disabled={submitting}
              placeholder={
                field === "email" ? "name@example.com" : "(404) 555-0123"
              }
            />
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <SheetFooter className="mt-6 flex-row justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleKeepAsIs}
              disabled={submitting || !entry}
            >
              Keep as-is
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {field === "email" ? "Save email" : "Save phone"}
              </Button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
